import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import {
  FlexColumn,
  Alert,
  Dialog,
  Link,
} from '@elementary/common-ui/components';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSso } from '../hooks/useSso';
import SsoConfigForm, { SsoConfigFormData } from './SsoConfigForm';

const isValidDomain = (domain: string) =>
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/.test(
    domain,
  );

const Content = styled(FlexColumn)`
  gap: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

const DialogContent = ({
  didSave,
  methods,
  domainValidationErrors,
  onDomainBlur,
  xmlError,
}: {
  didSave: boolean;
  methods: UseFormReturn<SsoConfigFormData>;
  domainValidationErrors: Array<string | null>;
  onDomainBlur: () => void;
  xmlError: string | null;
}) => {
  if (didSave) {
    return (
      <Content>
        <Alert severity="warning">
          Stay logged in while testing SSO. Verify login works in an incognito
          window or with another user before logging out.
        </Alert>
      </Content>
    );
  }
  return (
    <Content>
      <Typography>
        Allow your team to sign in with your identity provider.
        <br />
        Need help? Check out our{' '}
        <Link
          href="https://docs.elementary-data.com/cloud/integrations/security-and-connectivity/okta"
          target="_blank"
          rel="noopener"
        >
          docs
        </Link>{' '}
        for setup instructions.
      </Typography>
      <SsoConfigForm
        methods={methods}
        domainValidationErrors={domainValidationErrors}
        onDomainBlur={onDomainBlur}
        xmlError={xmlError}
      />
    </Content>
  );
};

const SsoConfigDialog = ({ open, onClose }: Props) => {
  const [didSave, setDidSave] = useState(false);
  const [xmlError, setXmlError] = useState<string | null>(null);
  const [domainValidationErrors, setDomainValidationErrors] = useState<
    Array<string | null>
  >([]);

  const sso = useSso();

  const defaultValues = useMemo(() => {
    const data: SsoConfigFormData = {
      metadataInputMethod: 'file',
      fileName: '',
      url: '',
      metadataXml: null,
      domains: [],
    };
    if (sso.data) {
      data.domains = sso.data.domains.map((d) => ({
        domain: d.domain,
        status: d.validated ? 'validated' : 'pending',
      }));
    }
    return data;
  }, [sso.data]);

  const methods = useForm<SsoConfigFormData>({
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
    }
  }, [open, defaultValues, methods]);

  useEffect(() => {
    if (open) {
      setDidSave(false);
      setXmlError(null);
      setDomainValidationErrors([]);
    }
  }, [open]);

  const metadataXml = methods.watch('metadataXml');

  useEffect(() => {
    setXmlError(null);
  }, [metadataXml]);

  const handleSave = useCallback(async () => {
    try {
      if (!sso.data) {
        await sso.create({
          metadataXml: methods.getValues('metadataXml') ?? '',
          domainNames: methods.getValues('domains').map((d) => d.domain),
        });
      } else {
        await sso.update({
          metadataXml: methods.getValues('metadataXml') ?? undefined,
          domainNames: methods.getValues('domains').map((d) => d.domain),
        });
      }
      setXmlError(null);
      setDidSave(true);
    } catch (error) {
      setXmlError(
        'Failed to save SSO configuration. Verify the provided metadata and try again',
      );
    }
  }, [sso, methods]);

  const domains = methods.watch('domains');
  const validateDomains = useCallback(() => {
    const errors = domains.map((domain) => {
      if (!domain.domain.trim()) {
        return 'Domain can not be empty';
      }
      if (!isValidDomain(domain.domain)) {
        return 'Invalid domain';
      }
      return null;
    });
    setDomainValidationErrors(errors);
  }, [domains, setDomainValidationErrors]);

  const isValid = useMemo(() => {
    if (xmlError) {
      return false;
    }
    const hasDomains = domains.length > 0;
    const areDomainsValid = !domainValidationErrors.some((v) => !!v);
    const isValid = areDomainsValid && hasDomains;
    if (sso.data) {
      return isValid;
    }
    return isValid && !!metadataXml;
  }, [metadataXml, sso.data, domains, domainValidationErrors, xmlError]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="SSO Configuration"
      message={
        <DialogContent
          didSave={didSave}
          methods={methods}
          domainValidationErrors={domainValidationErrors}
          onDomainBlur={validateDomains}
          xmlError={xmlError}
        />
      }
      confirmText={didSave ? 'OK' : 'Save'}
      onConfirmButtonClick={
        didSave ? onClose : methods.handleSubmit(handleSave)
      }
      confirmDisabled={!isValid}
      isLoading={sso.isLoading}
      actionButtonText={didSave ? undefined : 'Clear'}
      onActionButtonClick={
        didSave
          ? undefined
          : async () => {
              await sso.delete();
              onClose();
            }
      }
      hideCancelButton={didSave}
      actionButtonDisabled={sso.isLoading || !sso.data}
      actionButtonColor="error"
      actionButtonVariant="contained"
      customWidth={520}
    />
  );
};

export default SsoConfigDialog;
